import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { AlertCircle, ChevronDown, FolderOpen, Laptop } from 'lucide-react'
import { api } from '../api'
import { useAsync } from '../hooks'
import { Button, Field, TextInput } from './ui'
import { returnNotices } from './returnPresentation'
import './SharedReturnsSettings.css'

function TechnicalDetails({ details }: { details: string }) {
  const [open, setOpen] = useState(false)
  const id = useId()
  return <div className="return-details">
    <button type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpen(value => !value)}>
      Technical details <ChevronDown size={13} aria-hidden="true" className={open ? 'is-open' : ''} />
    </button>
    {open ? <pre id={id}>{details}</pre> : null}
  </div>
}

function ReturnIssue({ title, message, details, children }: { title?: string; message: string; details: string; children?: React.ReactNode }) {
  return <div className="return-issue" role="alert">
    <AlertCircle size={17} aria-hidden="true" />
    <div className="return-issue-copy">{title ? <strong>{title}</strong> : null}<p>{message}</p>
      {children}<TechnicalDetails key={details} details={details} />
    </div>
  </div>
}

export function SharedReturnsSettings() {
  const status = useAsync(api.sharedReturns, [], { pollMs: 3000 })
  const [root, setRoot] = useState('')
  const [shareId, setShareId] = useState('')
  const [setupOpen, setSetupOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<{ action: 'save' | 'clear'; details: string } | null>(null)
  const [readFailure, setReadFailure] = useState<string | null>(null)
  const failedReadData = useRef(status.data)
  const setupId = useId()
  // useAsync clears its error when retry starts but retains the old data.
  // Keep the failure visible until a successful read proves readiness again.
  useEffect(() => {
    if (status.error) {
      failedReadData.current = status.data
      setReadFailure(status.error)
    } else if (!status.loading && status.data && status.data !== failedReadData.current) setReadFailure(null)
  }, [status.error, status.loading, status.data])
  const failure = status.error || readFailure
  const desktopUnavailable = status.data !== null && 'available' in status.data && status.data.available === false
  const unavailable = Boolean(failure) || !status.data || desktopUnavailable
  const configured = Boolean(status.data?.configured)
  const issues = returnNotices(status.data)
  async function save(event: FormEvent) {
    event.preventDefault()
    if (busy || unavailable || !root.trim() || !shareId.trim()) return
    setBusy(true); setError(null)
    try { status.setData(await api.saveSharedReturns(root.trim(), shareId.trim())); await status.refresh() }
    catch (err) { setError({ action: 'save', details: err instanceof Error ? err.message : 'Return storage could not be saved.' }) }
    finally { setBusy(false) }
  }
  async function clear() {
    if (busy || unavailable) return
    setBusy(true); setError(null)
    try { status.setData(await api.clearSharedReturns()); await status.refresh() }
    catch (err) { setError({ action: 'clear', details: err instanceof Error ? err.message : 'Return storage could not be changed.' }) }
    finally { setBusy(false) }
  }
  return <section className="preferences-panel shared-returns-card" aria-label="Finished edit storage">
    <div className="return-storage-header">
      <span className="return-storage-icon" aria-hidden="true">{configured ? <FolderOpen size={20} /> : <Laptop size={20} />}</span>
      <div><h3>Finished edit storage</h3><p>Completed edits appear in Final Review.</p></div>
      {status.data && !failure && !desktopUnavailable ? <span className="return-storage-mode">{configured ? 'Shared folder' : 'This Mac'}</span> : null}
    </div>
    {failure ? <ReturnIssue message="Could not check return storage." details={failure}>
      <Button type="button" variant="secondary" size="sm" disabled={status.loading} onClick={() => void status.refresh()}>{status.loading ? 'Checking…' : 'Retry'}</Button>
    </ReturnIssue> : !status.data ? <p className="return-readiness" role="status">Checking return storage…</p>
      : desktopUnavailable ? <p className="return-readiness" role="status">Open Motion on the editor Mac to set up finished edit storage.</p>
      : status.data.ready ? <p className="return-readiness" role="status">{configured ? 'Finished edits return from your shared folder.' : 'Finished edits return to Motion from this Mac.'}</p>
        : <ReturnIssue title="Return storage needs attention" message={configured ? 'Check that the shared folder is connected and its identity matches your setup.' : 'Check that Motion can access its local return folder.'} details={status.data.message} />}
    {configured && status.data?.root && !failure && !desktopUnavailable ? <div className="return-configured-folder"><span>Export folder</span><code>{status.data.root}/returns/&lt;clip&gt;/&lt;approved version&gt;</code></div> : null}
    {!failure && !desktopUnavailable && issues.length ? <div className="return-issues">{issues.map(issue => <ReturnIssue key={issue.key} title={issue.title} message={issue.message} details={issue.details.join('\n\n')} />)}</div> : null}
    {!desktopUnavailable ? <div className="return-setup">
      <button type="button" className="return-setup-toggle" aria-expanded={setupOpen} aria-controls={setupId} onClick={() => setSetupOpen(value => !value)}>
        Shared folder setup <ChevronDown size={15} aria-hidden="true" className={setupOpen ? 'is-open' : ''} />
      </button>
      {setupOpen ? <form id={setupId} className="return-setup-form" onSubmit={event => void save(event)} aria-label="Shared folder setup">
        <p>Use a mounted shared folder when finished edits are stored outside this Mac.</p>
        <fieldset disabled={busy || unavailable}>
          <Field label="Mounted finished edit folder"><TextInput aria-label="Mounted finished edit folder" value={root} placeholder="/Volumes/Production/Finished" onChange={event => setRoot(event.target.value)} /></Field>
          <Field label="Share ID" hint="Use the ID from the folder’s .motion-return-share.json identity file."><TextInput aria-label="Share ID" value={shareId} placeholder="Share identity UUID" onChange={event => setShareId(event.target.value)} /></Field>
          <div className="return-setup-actions"><Button type="submit" disabled={!root.trim() || !shareId.trim()}>{busy ? 'Saving…' : 'Save return folder'}</Button>
            {configured ? <Button type="button" variant="secondary" onClick={() => void clear()}>Use this Mac</Button> : null}</div>
        </fieldset>
        <p className="return-setup-hint">The folder must already be mounted and have a matching identity file. Exports need a .done sidecar after writing finishes. Use the clip and version filename from the Premiere handoff.</p>
      </form> : null}
    </div> : null}
    {error ? <ReturnIssue message={error.action === 'save' ? 'Return folder could not be saved.' : 'Could not switch to this Mac.'} details={error.details} /> : null}
  </section>
}
